import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from 'react-aria-components/Button';
import { Dialog } from 'react-aria-components/Dialog';
import { Modal, ModalOverlay } from 'react-aria-components/Modal';

export interface AccessibleDialogProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  closeLabel?: string;
  showCloseButton?: boolean;
  isDismissable?: boolean;
  isKeyboardDismissDisabled?: boolean;
  placement?: 'center' | 'end';
  size?: 'normal' | 'wide';
  id?: string;
}

/**
 * Diálogo modal común del sitio. React Aria aporta la contención del foco,
 * Escape, ocultamiento del fondo y retorno al control que lo abrió.
 */
export function AccessibleDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  closeLabel = 'Cerrar',
  showCloseButton = true,
  isDismissable = false,
  isKeyboardDismissDisabled = false,
  placement = 'center',
  size = 'normal',
  id,
}: AccessibleDialogProps) {
  const generatedId = useId();
  const dialogId = id ?? generatedId;
  const titleId = `${dialogId}-title`;
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  // Los diálogos controlados no siempre viven dentro de un DialogTrigger.
  // Conservamos el origen aquí para que todos, también los abiertos por un
  // cambio de estado, cumplan el mismo retorno de foco.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else if (!isOpen && wasOpenRef.current) {
      const opener = openerRef.current;
      window.setTimeout(() => {
        if (opener?.isConnected) opener.focus();
      }, 0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <ModalOverlay
      className={`accessible-dialog-overlay accessible-dialog-overlay--${placement}`}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
    >
      <Modal className={`accessible-dialog-modal accessible-dialog-modal--${size}`}>
        <Dialog id={dialogId} className="accessible-dialog-panel" aria-labelledby={titleId}>
          <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
            <h2 id={titleId} className="h5 mb-0">{title}</h2>
            {showCloseButton ? (
              <Button slot="close" className="btn-close" aria-label={closeLabel} />
            ) : null}
          </div>
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

interface AccessibleConfirmDialogProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm(): void;
  cancelLabel?: string;
  isPending?: boolean;
  danger?: boolean;
}

/** Confirmación que enfoca primero la salida segura y nunca la acción destructiva. */
export function AccessibleConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  onConfirm,
  cancelLabel = 'Cancelar',
  isPending = false,
  danger = true,
}: AccessibleConfirmDialogProps) {
  return (
    <AccessibleDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      showCloseButton={false}
      isKeyboardDismissDisabled={isPending}
    >
      {children}
      <div className="accessible-dialog-actions">
        <Button autoFocus className="btn btn-outline-primary" isDisabled={isPending} onPress={() => onOpenChange(false)}>
          {cancelLabel}
        </Button>
        <Button className={danger ? 'btn btn-outline-danger' : 'btn btn-accent'} isPending={isPending} onPress={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </AccessibleDialog>
  );
}
